// Canonical Google Analytics 4 service (consent-managed, single GA4 path).
//
// This module is the ONLY place GA4 is wired into the application. It:
//   - loads the GA4 tag lazily and at most once (no inline tag in index.html),
//   - sets Google Consent Mode v2 default-denied so nothing fires pre-consent,
//   - gates every event behind the privacy consent manager,
//   - deduplicates consecutive page_view calls so pageviews are never
//     double-counted (SPA navigations, React StrictMode, HMR, etc.).
//
// Consumers must import from here (or via the back-compat re-export at
// src/utils/analytics/googleAnalytics.ts). There is no other GA4 path.

import { privacyConsent } from '../utils/analytics/privacyConsent';
import type { ConsentPreferences } from '../utils/analytics/privacyConsent';

// GA4 measurement ID configuration.
//
// The measurement ID is configurable via the VITE_GA_MEASUREMENT_ID build-time
// environment variable (see .env.production / .env.staging). The constant
// below is a fallback used when the variable is not provided. Resolving the
// ID through a getter (rather than a single hardcode) keeps the GA4 tag out of
// index.html and lets the value be overridden per-deployment without touching
// source.
export const GA_MEASUREMENT_ID_FALLBACK = 'G-984VQC7Z6Q';

export const getGaMeasurementId = (): string => {
  const envId = import.meta.env.VITE_GA_MEASUREMENT_ID?.trim();
  return envId && envId.length > 0 ? envId : GA_MEASUREMENT_ID_FALLBACK;
};

// Snapshot resolved once at module load for back-compat consumers that import
// the ID as a plain value. The class itself calls getGaMeasurementId() so the
// tag src, config and send_to reflect a live env override.
export const GA_MEASUREMENT_ID = getGaMeasurementId();

const gtagSrc = (id: string): string =>
  `https://www.googletagmanager.com/gtag/js?id=${id}`;
const SCRIPT_DATA_ATTR = 'data-rt-ga4';

type GtagArgs = unknown[];

interface WindowWithGtag extends Window {
  dataLayer?: unknown[];
  gtag?: (...args: GtagArgs) => void;
}

interface AnalyticsState {
  initialized: boolean;
  scriptInjected: boolean;
  consentGranted: boolean;
  lastFiredPageViewLocation: string | null;
  pendingPageView: { title: string; location: string } | null;
}

class AnalyticsService {
  private state: AnalyticsState = {
    initialized: false,
    scriptInjected: false,
    consentGranted: false,
    lastFiredPageViewLocation: null,
    pendingPageView: null,
  };

  private unsubscribeConsent: (() => void) | null = null;

  // Idempotent bootstrap. Safe to call multiple times.
  init(): void {
    if (typeof window === 'undefined') return;
    if (this.state.initialized) return;
    this.state.initialized = true;

    const w = window as WindowWithGtag;

    w.dataLayer = w.dataLayer || [];
    if (typeof w.gtag !== 'function') {
      w.gtag = function (...args: GtagArgs): void {
        w.dataLayer!.push(args);
      };
    }

    const g = w.gtag!;

    g('consent', 'default', {
      analytics_storage: 'granted',
      ad_storage: 'denied',
      functionality_storage: 'granted',
      personalization_storage: 'denied',
      security_storage: 'granted',
      wait_for_update: 500,
    });

    g('config', getGaMeasurementId(), {
      anonymize_ip: true,
      allow_google_signals: false,
      allow_ad_personalization_signals: false,
      send_page_view: false,
    });

    this.injectScript();

    this.state.consentGranted = privacyConsent.isConsentGranted('analytics');
    this.unsubscribeConsent = privacyConsent.subscribe((prefs: ConsentPreferences) => {
      this.onConsentChange(prefs.analytics);
    });
  }

  private injectScript(): void {
    if (typeof document === 'undefined') return;
    if (this.state.scriptInjected) return;
    if (document.querySelector(`script[${SCRIPT_DATA_ATTR}]`)) {
      this.state.scriptInjected = true;
      return;
    }
    const script = document.createElement('script');
    script.async = true;
    script.setAttribute(SCRIPT_DATA_ATTR, 'ga4');
    script.src = gtagSrc(getGaMeasurementId());
    document.head.appendChild(script);
    this.state.scriptInjected = true;
  }

  private onConsentChange(granted: boolean): void {
    const w = window as WindowWithGtag;
    if (typeof w.gtag === 'function') {
      w.gtag('consent', 'update', {
        analytics_storage: granted ? 'granted' : 'denied',
      });
    }
    const wasGranted = this.state.consentGranted;
    this.state.consentGranted = granted;
    if (granted && !wasGranted) {
      this.flushPendingPageView();
    }
  }
  // Update consent programmatically (back-compat for setAnalyticsConsent).
  updateConsent(granted: boolean): void {
    const current = privacyConsent.isConsentGranted('analytics');
    if (current === granted) {
      this.state.consentGranted = granted;
      return;
    }
    privacyConsent.updatePreference('analytics', granted);
  }

  isConsentGranted(): boolean {
    return privacyConsent.isConsentGranted('analytics');
  }

  hasConsentChoice(): boolean {
    return privacyConsent.hasConsented();
  }

  private getGtag(): ((...args: GtagArgs) => void) | null {
    if (typeof window === 'undefined') return null;
    const w = window as WindowWithGtag;
    return typeof w.gtag === 'function' ? w.gtag : null;
  }

  private flushPendingPageView(): void {
    const pending = this.state.pendingPageView;
    this.state.pendingPageView = null;
    if (!pending) return;
    if (this.state.lastFiredPageViewLocation === pending.location) return;
    this.state.lastFiredPageViewLocation = pending.location;
    this.firePageView(pending.title, pending.location);
  }

  private firePageView(pageTitle: string, pageLocation: string): void {
    const g = this.getGtag();
    if (!g) return;
    g('event', 'page_view', {
      page_title: pageTitle,
      page_location: pageLocation,
      send_to: getGaMeasurementId(),
    });
  }

  trackPageView(pageTitle: string, pageLocation?: string): void {
    this.init();
    const location = pageLocation || (typeof window !== 'undefined' ? window.location.href : '');

    if (!this.isConsentGranted()) {
      // Remember as pending so exactly one page_view fires when consent is granted.
      this.state.pendingPageView = { title: pageTitle, location };
      return;
    }

    // Dedupe consecutive page_views for the same location (SPA, StrictMode, HMR).
    if (this.state.lastFiredPageViewLocation === location) {
      return;
    }
    this.state.lastFiredPageViewLocation = location;
    this.firePageView(pageTitle, location);
  }

  trackEvent(event: {
    event_name: string;
    event_category?: string;
    event_label?: string;
    value?: number;
    custom_parameters?: Record<string, unknown>;
  }): void {
    this.init();

    if (!this.isConsentGranted()) {
      return;
    }

    const g = this.getGtag();
    if (!g) return;

    const eventData: Record<string, unknown> = {
      event_category: event.event_category || 'engagement',
      event_label: event.event_label,
      value: event.value,
      ...event.custom_parameters,
    };

    Object.keys(eventData).forEach(key => {
      if (eventData[key] === undefined) {
        delete eventData[key];
      }
    });

    g('event', event.event_name, eventData);
  }

  trackTabSwitch(fromTab: string, toTab: string): void {
    this.trackEvent({
      event_name: 'tab_switch',
      event_category: 'navigation',
      event_label: `${fromTab}_to_${toTab}`,
    });
  }

  trackCalculation(
    calculationType: 'basic' | 'advanced',
    incomeFrequency: 'monthly' | 'yearly'
  ): void {
    this.trackEvent({
      event_name: 'tax_calculation',
      event_category: 'calculator',
      event_label: calculationType,
      custom_parameters: {
        income_frequency: incomeFrequency,
        calculation_type: calculationType,
      },
    });
  }

  trackAdvancedOption(
    optionType: 'medical_aid' | 'pension' | 'travel_allowance',
    enabled: boolean
  ): void {
    this.trackEvent({
      event_name: 'advanced_option_used',
      event_category: 'calculator',
      event_label: optionType,
      custom_parameters: {
        option_type: optionType,
        enabled,
      },
    });
  }

  trackExportAction(
    exportType: 'pdf' | 'csv' | 'text' | 'print' | 'share',
    exportSource: string
  ): void {
    this.trackEvent({
      event_name: 'export_action',
      event_category: 'export',
      event_label: exportType,
      custom_parameters: {
        export_type: exportType,
        export_source: exportSource,
      },
    });
  }

  trackSocialShare(platform: string, contentType: string): void {
    this.trackEvent({
      event_name: 'social_share',
      event_category: 'share',
      event_label: platform,
      custom_parameters: {
        platform,
        content_type: contentType,
      },
    });
  }

  trackEducationInteraction(
    interactionType: 'faq_open' | 'faq_close' | 'tax_year_change',
    contentId?: string
  ): void {
    this.trackEvent({
      event_name: 'education_interaction',
      event_category: 'education',
      event_label: interactionType,
      custom_parameters: {
        interaction_type: interactionType,
        content_id: contentId,
      },
    });
  }

  trackScenarioAction(
    action: 'save' | 'edit' | 'delete' | 'compare',
    scenarioCount?: number
  ): void {
    this.trackEvent({
      event_name: 'scenario_action',
      event_category: 'scenarios',
      event_label: action,
      custom_parameters: {
        action,
        scenario_count: scenarioCount,
      },
    });
  }

  trackPerformance(metricName: string, value: number, unit: string): void {
    this.trackEvent({
      event_name: 'performance_metric',
      event_category: 'performance',
      event_label: metricName,
      value: Math.round(value),
      custom_parameters: {
        metric_name: metricName,
        value: Math.round(value),
        unit,
      },
    });
  }

  // Safe accessor for the referring URL. Read once per call so the value
  // reflects whatever the browser currently exposes on document.referrer.
  getDocumentReferrer(): string {
    if (typeof document === 'undefined') return '';
    return typeof document.referrer === 'string' ? document.referrer : '';
  }

  // fx-1.2: fires `calc_run` on each completed reverse-tax calculation.
  // Carries the selected tax year and document.referrer as event parameters.
  // No PII and no monetary amounts are included.
  trackCalcRun(payload: {
    taxYear: string;
    calcMode?: 'reverse' | 'forward';
  }): void {
    this.trackEvent({
      event_name: 'calc_run',
      event_category: 'calculator',
      event_label: payload.calcMode ?? 'reverse',
      custom_parameters: {
        tax_year: payload.taxYear,
        calc_mode: payload.calcMode ?? 'reverse',
        referrer: this.getDocumentReferrer(),
      },
    });
  }

  // fx-1.2: fires `calc_options_used` carrying which options were used
  // (age band, frequency, medical/pension inputs) as a non-PII payload.
  // Only categorical/boolean signals are sent — never income or amounts.
  trackCalcOptionsUsed(payload: {
    ageBand: string;
    incomeFrequency: 'monthly' | 'yearly';
    hasMedicalAid: boolean;
    hasPension: boolean;
    hasTravelAllowance: boolean;
    calculationType: 'basic' | 'advanced';
    medicalDependents?: number;
  }): void {
    const params: Record<string, unknown> = {
      age_band: payload.ageBand,
      income_frequency: payload.incomeFrequency,
      has_medical_aid: payload.hasMedicalAid,
      has_pension: payload.hasPension,
      has_travel_allowance: payload.hasTravelAllowance,
      calculation_type: payload.calculationType,
    };
    if (payload.medicalDependents !== undefined) {
      params.medical_dependents = payload.medicalDependents;
    }
    this.trackEvent({
      event_name: 'calc_options_used',
      event_category: 'calculator',
      custom_parameters: params,
    });
  }

  // Test/diagnostics helper: reset internal dedupe + init flags. Not for prod hot paths.
  __resetForTesting(): void {
    this.state = {
      initialized: false,
      scriptInjected: false,
      consentGranted: false,
      lastFiredPageViewLocation: null,
      pendingPageView: null,
    };
    if (this.unsubscribeConsent) {
      this.unsubscribeConsent();
      this.unsubscribeConsent = null;
    }
    if (typeof window !== 'undefined') {
      const w = window as WindowWithGtag;
      w.gtag = undefined;
      w.dataLayer = [];
    }
  }
}

export const analyticsService = new AnalyticsService();

export const initializeGA = (): void => {
  analyticsService.init();
};

export const initializeConsent = (): void => {
  analyticsService.init();
};

export const trackEvent = (event: Parameters<AnalyticsService['trackEvent']>[0]): void =>
  analyticsService.trackEvent(event);

export const trackPageView = (
  pageTitle: string,
  pageLocation?: string
): void => analyticsService.trackPageView(pageTitle, pageLocation);

export const trackTabSwitch = (fromTab: string, toTab: string): void =>
  analyticsService.trackTabSwitch(fromTab, toTab);

export const trackCalculation = (
  calculationType: 'basic' | 'advanced',
  incomeFrequency: 'monthly' | 'yearly'
): void => analyticsService.trackCalculation(calculationType, incomeFrequency);

export const trackAdvancedOption = (
  optionType: 'medical_aid' | 'pension' | 'travel_allowance',
  enabled: boolean
): void => analyticsService.trackAdvancedOption(optionType, enabled);

export const trackExportAction = (
  exportType: 'pdf' | 'csv' | 'text' | 'print' | 'share',
  exportSource: string
): void => analyticsService.trackExportAction(exportType, exportSource);

export const trackSocialShare = (platform: string, contentType: string): void =>
  analyticsService.trackSocialShare(platform, contentType);

export const trackEducationInteraction = (
  interactionType: 'faq_open' | 'faq_close' | 'tax_year_change',
  contentId?: string
): void => analyticsService.trackEducationInteraction(interactionType, contentId);

export const trackScenarioAction = (
  action: 'save' | 'edit' | 'delete' | 'compare',
  scenarioCount?: number
): void => analyticsService.trackScenarioAction(action, scenarioCount);

export const trackPerformance = (metricName: string, value: number, unit: string): void =>
  analyticsService.trackPerformance(metricName, value, unit);

export const getDocumentReferrer = (): string => analyticsService.getDocumentReferrer();

export const trackCalcRun = (payload: {
  taxYear: string;
  calcMode?: 'reverse' | 'forward';
}): void => analyticsService.trackCalcRun(payload);

export const trackCalcOptionsUsed = (payload: {
  ageBand: string;
  incomeFrequency: 'monthly' | 'yearly';
  hasMedicalAid: boolean;
  hasPension: boolean;
  hasTravelAllowance: boolean;
  calculationType: 'basic' | 'advanced';
  medicalDependents?: number;
}): void => analyticsService.trackCalcOptionsUsed(payload);

export const setAnalyticsConsent = (granted: boolean): void => {
  analyticsService.updateConsent(granted);
};

export const getAnalyticsConsent = (): boolean => analyticsService.isConsentGranted();

export const hasConsentChoice = (): boolean => analyticsService.hasConsentChoice();
